import React, {useCallback, useState} from 'react';
import {Col, Container, Row, Tab, Tabs} from 'react-bootstrap';
import styled from 'styled-components';
import {Checkout} from 'components/customer/Confirmation/Checkout';
import {Sidebar} from 'components/customer/Confirmation/Sidebar';
import {Footer} from 'components/customer/Confirmation/Footer';
import {mobile} from 'shared/Breakpoints';
import {ControlButtons} from 'components/customer/Confirmation/ControlButtons';
import {
    ConfirmationKey,
    ConfirmationTabTitle,
} from 'components/customer/Confirmation/ConfirmationTabTitle';
import {Freight} from 'components/customer/Confirmation/Freight';
import {useSearchParams} from 'react-router-dom';
import {useDisableCheckout} from 'components/customer/Confirmation/helpers/selectDisableCheckout';
import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {JobHeader} from 'components/customer/Confirmation/JobHeader';
import {useCaptureDeliveryInfo} from 'components/customer/Confirmation/helpers/useCaptureDeliveryInfo';
import {useAutoScrollToTop} from 'shared/helpers/useAutoScrollToTop';
import {useSaveJob} from 'components/customer/Job/helpers/useSaveJob';
import {Loader} from 'shared/helpers';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';

export const Confirmation = () => {
    const [searchParams] = useSearchParams();
    const disableCheckout = useDisableCheckout();
    const captureDeliveryInfo = useCaptureDeliveryInfo();
    const {job} = useJobContext() as PartialJob;
    const isJobLoaded = !!job?.id;
    const [key, setKey] = useState(
        !captureDeliveryInfo || (searchParams && searchParams.has('checkout'))
            ? ConfirmationKey.CHECKOUT
            : ConfirmationKey.FREIGHT
    );
    const {containerRef} = useAutoScrollToTop<HTMLDivElement>([key]);

    // selecting pickup or freight changes the freight cost, so the delivery
    // details are saved before the checkout tab shows the cost. nothing is
    // saved if they have not changed, and a failed save stays on the freight tab
    const {saveJob} = useSaveJob();
    const [saving, setSaving] = useState(false);

    const goToCheckout = useCallback(async () => {
        setSaving(true);

        try {
            if (await saveJob()) {
                setKey(ConfirmationKey.CHECKOUT);
            }
        } finally {
            setSaving(false);
        }
    }, [saveJob]);

    const handleCheckout = useCallback(() => {
        void goToCheckout();
    }, [goToCheckout]);

    const handleActiveTab = useCallback(
        (eventKey: string | null) => {
            if (eventKey == ConfirmationKey.CHECKOUT) {
                void goToCheckout();
            } else if (eventKey) {
                setKey(eventKey as ConfirmationKey);
            }
        },
        [goToCheckout]
    );

    return (
        <ConfirmationContainer
            ref={containerRef}
            $clamp={true}
            $noMargin={false}
            className="cbc-form">
            <ControlButtons smallDevice={true} />
            <Container fluid>
                <Row>
                    <MainCol>
                        {!isJobLoaded ? null : !captureDeliveryInfo ? (
                            <>
                                <JobHeader />
                                <Checkout />
                            </>
                        ) : (
                            <ConfirmationTabs
                                variant="pills"
                                activeKey={key}
                                onSelect={handleActiveTab}
                                transition={true}>
                                <Tab
                                    eventKey={ConfirmationKey.FREIGHT}
                                    title={
                                        <ConfirmationTabTitle
                                            isActive={
                                                key == ConfirmationKey.FREIGHT
                                            }
                                            activeIcon="Delivery-Default-White.svg"
                                            icon="Delivery-Default.svg"
                                            title="Pickup / Freight"
                                        />
                                    }>
                                    <JobHeader />
                                    <Freight />
                                </Tab>
                                <Tab
                                    disabled={disableCheckout}
                                    eventKey={ConfirmationKey.CHECKOUT}
                                    title={
                                        <ConfirmationTabTitle
                                            isActive={
                                                key == ConfirmationKey.CHECKOUT
                                            }
                                            activeIcon="Submit-white.svg"
                                            icon="Submit-secondary.svg"
                                            title="Checkout"
                                        />
                                    }>
                                    <JobHeader />
                                    <Checkout />
                                </Tab>
                            </ConfirmationTabs>
                        )}
                    </MainCol>
                    <Col lg={3} md={12}>
                        <Sidebar />
                    </Col>
                </Row>
            </Container>

            <Loader loader={saving || !isJobLoaded} />
            <Footer
                currentTab={
                    captureDeliveryInfo ? key : ConfirmationKey.CHECKOUT
                }
                setCurrentTab={setKey}
                onCheckout={handleCheckout}
            />
        </ConfirmationContainer>
    );
};

const MainCol = styled(Col)`
    min-width: 0;
`;

const ConfirmationTabs = styled(Tabs)`
    &.nav-pills {
        display: flex;
        width: fit-content;
        margin: 0 auto;
        position: relative;
        gap: 15px;

        &:after {
            content: '';
            position: absolute;
            width: 100%;
            top: calc(50% - 1px);
            z-index: -1;
            border: 1px dotted ${({theme}) => theme.colors.secondary?.main};
        }
    }

    .nav-link {
        border: 0;
        border-radius: 26px;
        color: ${({theme}) => theme.colors.secondary?.main};
        font-weight: 500;
        font-size: 1em;
        width: 250px;
        background: white;
        padding: 0;

        > div {
            border: 2px solid ${({theme}) => theme.colors.secondary?.main};
            border-radius: 26px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 2px;

            > img {
                width: 35px;
            }

            > span {
                flex: 1;
                text-align: center;
            }
        }

        &:hover,
        &:active,
        &:focus {
            background: ${({theme}) => theme.colors.secondary?.main} !important;
            color: white !important;
        }

        &.disabled {
            border-color: ${({theme}) => lightenColor(theme.colors.secondary?.main, 40)} !important;
            color: ${({theme}) => lightenColor(theme.colors.secondary?.main, 40)} !important;
            cursor: not-allowed;
        }
    }

    .nav-link.active {
        background: ${({theme}) => theme.colors.secondary?.main} !important;
        color: white;
    }

    ${mobile`
        &.nav-pills {
            margin-top: 15px;
            width: stretch;
            justify-content: space-between;
        }

        .nav-item {
            width: calc(50% - 15px);
        }

        .nav-link {
            font-weight: 500;
            font-size: 0.8em;
            width: stretch;

            > div {
                gap: 10px;
                padding-right: 8px;

                > img {
                    width: 27px;
                }
            }
        }
    `}
`;

export const ConfirmationContainer = styled.div<{
    $clamp?: boolean;
    $noMargin?: boolean;
}>`
    ${({$clamp, $noMargin = true}) => {
        if ($clamp) {
            return `
                width: 90%;
                margin: ${$noMargin ? '0 auto' : '0 auto 140px'};
                flex: initial !important;
            `;
        }
    }}

    ${mobile`
        width: 100%;
        margin-bottom: 15px;

        > .container-fluid {
            padding: 0;
        }
    `};
`;
